import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Coins, Video } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { scrollToSection } from "@/components/SmoothScroll";

const PRISM_IMG =
  "https://images.unsplash.com/photo-1649290098499-f4148542f2e0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwyfHxnbGFzcyUyMHByaXNtJTIwbGlnaHQlMjByZWZyYWN0aW9ufGVufDB8fHx8MTc5Mzc5Mjk4N3ww&ixlib=rb-4.1.0&q=85";
const RAINBOW_IMG =
  "https://images.unsplash.com/photo-1579546929518-9e396f3cc809?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwxfHxyYWluYm93JTIwbGlnaHQlMjBzcGVjdHJ1bSUyMGFic3RyYWN0JTIwc29mdHxlbnwwfHx8fDE3OTM3OTI5ODh8MA&ixlib=rb-4.1.0&q=85";

const RainbowDraw = () => (
  <motion.svg
    viewBox="0 0 120 40"
    fill="none"
    className="h-8 w-24"
    initial="hidden"
    animate="visible"
    aria-hidden="true"
  >
    {[
      { d: "M6 38a54 54 0 0 1 108 0", c: "#FF6B6B", w: 3.2 },
      { d: "M18 38a42 42 0 0 1 84 0", c: "#F9D876", w: 3.2 },
      { d: "M30 38a30 30 0 0 1 60 0", c: "#4ECDC4", w: 3.2 },
      { d: "M42 38a18 18 0 0 1 36 0", c: "#45B7D1", w: 3.2 },
    ].map((arc, i) => (
      <motion.path
        key={arc.d}
        d={arc.d}
        stroke={arc.c}
        strokeWidth={arc.w}
        strokeLinecap="round"
        variants={{ hidden: { pathLength: 0 }, visible: { pathLength: 1 } }}
        transition={{ duration: 1.4, delay: 0.15 + i * 0.16, ease: [0.22, 1, 0.36, 1] }}
      />
    ))}
  </motion.svg>
);

const Hero = () => {
  const { t } = useLanguage();
  const { scrollY } = useScroll();
  const yImage = useTransform(scrollY, [0, 700], [0, 70]);
  const yOrb = useTransform(scrollY, [0, 700], [0, -90]);

  return (
    <section id="top" data-testid="section-hero" className="relative overflow-hidden pb-20 pt-32 md:pb-28 md:pt-44">
      <motion.div
        style={{ y: yOrb }}
        className="pointer-events-none absolute -top-32 right-[-10%] h-[34rem] w-[34rem] rounded-full bg-gradient-to-br from-[#FECFEF] via-[#4ECDC4]/40 to-[#45B7D1]/40 blur-3xl"
        aria-hidden="true"
      />
      <div className="pointer-events-none absolute bottom-[-12rem] left-[-8%] h-[28rem] w-[28rem] rounded-full bg-gradient-to-tr from-[#FF6B6B]/30 via-[#F9D876]/30 to-transparent blur-3xl" aria-hidden="true" />

      <div className="mx-auto grid max-w-7xl items-center gap-16 px-6 lg:grid-cols-12 lg:gap-10">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            className="flex items-center gap-4"
          >
            <RainbowDraw />
            <p className="text-xs font-medium uppercase tracking-[0.28em] text-[#5C5C5C]">{t.hero.eyebrow}</p>
          </motion.div>

          <h1 className="mt-8 font-display text-[2.9rem] font-medium leading-[1.04] tracking-tight text-[#1A1A1A] sm:text-6xl lg:text-7xl xl:text-[5.4rem]">
            {t.hero.lines.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span
                  className="block"
                  initial={{ y: "112%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 1, delay: 0.3 + i * 0.14, ease: [0.22, 1, 0.36, 1] }}
                >
                  {line.em ? (
                    <>
                      {line.pre}
                      <em className="text-rainbow pr-2 italic">{line.em}</em>
                    </>
                  ) : (
                    line.text
                  )}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.85, ease: [0.22, 1, 0.36, 1] }}
            className="mt-7 max-w-xl text-base leading-relaxed text-[#5C5C5C] md:text-lg"
          >
            {t.hero.sub}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1, ease: [0.22, 1, 0.36, 1] }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <motion.button
              type="button"
              data-testid="hero-cta-contact"
              onClick={() => scrollToSection("#contact")}
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.97 }}
              className="group inline-flex items-center gap-3 rounded-full bg-[#1A1A1A] px-8 py-4 text-sm font-medium text-[#FDFBF7] transition-colors duration-300 hover:bg-[#333]"
            >
              {t.hero.ctaPrimary}
              <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-0.5" />
            </motion.button>
            <motion.button
              type="button"
              data-testid="hero-cta-courses"
              onClick={() => scrollToSection("#limbi")}
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.97 }}
              className="inline-flex items-center gap-3 rounded-full border border-black/15 bg-white/60 px-8 py-4 text-sm font-medium text-[#1A1A1A] backdrop-blur transition-colors duration-300 hover:border-black/30"
            >
              {t.hero.ctaSecondary}
            </motion.button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.2 }}
            className="mt-8 inline-flex items-center gap-2.5 rounded-full border border-black/[0.08] bg-white/70 px-5 py-2.5 backdrop-blur"
          >
            <Coins className="h-4 w-4 text-[#9B5DE5]" />
            <span className="text-sm font-medium text-[#1A1A1A]">{t.hero.chip}</span>
          </motion.div>
        </div>

        <div className="relative lg:col-span-5">
          <motion.div
            style={{ y: yImage }}
            initial={{ opacity: 0, scale: 0.96, y: 40 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 1.1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="relative mx-auto max-w-md"
          >
            <div className="overflow-hidden rounded-b-[2rem] rounded-t-[14rem] border border-black/[0.06] shadow-[0_24px_60px_rgb(0,0,0,0.10)]">
              <img
                src={PRISM_IMG}
                alt="Glass prisms refracting light into a rainbow spectrum"
                className="aspect-[4/5] w-full object-cover"
                loading="eager"
              />
            </div>

            <motion.img
              src={RAINBOW_IMG}
              alt="Soft rainbow light spectrum"
              initial={{ opacity: 0, scale: 0.6, rotate: -8 }}
              animate={{ opacity: 1, scale: 1, rotate: 0 }}
              transition={{ duration: 0.9, delay: 1.15, ease: [0.22, 1, 0.36, 1] }}
              className="absolute -right-6 -top-6 h-28 w-28 rounded-full border-4 border-white object-cover shadow-[0_12px_32px_rgb(0,0,0,0.12)] md:-right-10"
              data-testid="hero-rainbow-accent"
            />

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.3, ease: [0.22, 1, 0.36, 1] }}
              className="absolute -bottom-6 left-1/2 flex w-[85%] -translate-x-1/2 items-center gap-4 rounded-3xl border border-black/[0.06] bg-white/80 p-5 shadow-[0_16px_40px_rgb(0,0,0,0.08)] backdrop-blur-xl md:left-auto md:right-[-2.5rem] md:w-auto md:translate-x-0"
              data-testid="hero-online-card"
            >
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-[#FF6B6B] via-[#F9D876] to-[#4ECDC4]">
                <Video className="h-5 w-5 text-white" />
              </span>
              <span>
                <span className="block font-display text-xl font-semibold text-[#1A1A1A]">{t.hero.card.title}</span>
                <span className="block text-sm text-[#5C5C5C]">{t.hero.card.sub}</span>
              </span>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
